import { useEffect, useState } from 'react';

import { getTabAssignment } from '@/auth/activeAssignment';

import { type TenantCapabilities, getTenantCapabilities } from './api';

export type { TenantCapabilities } from './api';

/**
 * What the tenant administration offers this role (Set-up, Invite, History,
 * Approvals), from `GET admin/tenant/capabilities`: the identity service
 * answers it with 200 for every signed-in user, so no settings page load
 * produces a refusal. Everything else hides the new parts:
 * - 404 / 405: an identity service older than the tenant administration;
 * - 503 tenant_administration_unavailable: its database update has not run;
 * - a server error or nothing answered (asked again on the next use).
 *
 * Kept in memory only, per active role assignment (a role switch asks again;
 * the page reloads on a switch anyway). It decides only what is SHOWN; the
 * server decides every action again.
 */
export const NO_TENANT_CAPABILITIES: TenantCapabilities = {
  setup: false,
  invite: false,
  history: false,
  approve: false,
  rules: false,
  memberships: false,
  my_requests: false,
};

interface Cached {
  assignmentId: number | null;
  answer: Promise<TenantCapabilities>;
}

let cached: Cached | null = null;

function forget(entry: Cached): void {
  if (cached === entry) cached = null;
}

export function tenantCapabilities(): Promise<TenantCapabilities> {
  const assignmentId = getTabAssignment()?.id ?? null;
  if (!cached || cached.assignmentId !== assignmentId) {
    const entry: Cached = { assignmentId, answer: Promise.resolve(NO_TENANT_CAPABILITIES) };
    entry.answer = getTenantCapabilities().then(
      (r): TenantCapabilities => {
        if (r.ok) return r.data;
        // nothing answered or a server error: asked again on the next use, not remembered
        if (r.refusal.status === 0 || r.refusal.status >= 500) forget(entry);
        return NO_TENANT_CAPABILITIES;
      },
      (): TenantCapabilities => {
        forget(entry);
        return NO_TENANT_CAPABILITIES;
      },
    );
    cached = entry;
  }
  return cached.answer;
}

/** Test helper; a role switch resets by itself (the cache is per active assignment). */
export function resetTenantAdminProbeForTests(): void {
  cached = null;
}

/**
 * React: the capabilities once known (`null` while unknown, or when
 * `enabled` is false: no call is made then).
 */
export function useTenantCapabilities(enabled = true): TenantCapabilities | null {
  const [answer, setAnswer] = useState<TenantCapabilities | null>(null);
  useEffect(() => {
    if (!enabled) {
      setAnswer(null);
      return undefined;
    }
    let alive = true;
    void tenantCapabilities().then((a) => {
      if (alive) setAnswer(a);
    });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return answer;
}

/** One capability: false while unknown, unavailable or not held. */
export function useTenantCapability(key: keyof TenantCapabilities, enabled = true): boolean {
  const caps = useTenantCapabilities(enabled);
  return Boolean(caps?.[key]);
}
